import React, { useState } from 'react';
import { Plus, Settings, ShieldCheck, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { VoiceLayout } from './VoiceLayout';
import { AI_CAMPAIGNS, VOICE_AGENTS } from '@/data/voiceAgents';
import { usePageTitle } from '@/lib/usePageTitle';
import { formatNumber, formatSAR } from '@/lib/format';
import { cn } from '@/lib/utils';

const STATUS_TONE = {
  Active:             'border-success/40 bg-success/10 text-success',
  Paused:             'border-info/40 bg-info/10 text-info',
  'Pending Approval': 'border-warning/40 bg-warning/10 text-warning',
};

export default function CampaignAssignment() {
  usePageTitle('Campaign Assignment');
  const [drawer, setDrawer] = useState(null);

  return (
    <VoiceLayout
      title="Campaign assignment"
      subtitle="Assign AI voice agents to debtor segments, configure contact strategy, fallback channels and supervisor approval thresholds per campaign."
      actions={(<Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90"><Plus className="h-4 w-4 mr-1.5" aria-hidden="true" />Create campaign</Button>)}
    >
      <div className="surface-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-muted-foreground text-[11px] uppercase tracking-widest">
            <tr>
              <th scope="col" className="text-left px-3 py-2.5 font-medium">Campaign</th>
              <th scope="col" className="text-left px-3 py-2.5 font-medium">Client / Portfolio</th>
              <th scope="col" className="text-left px-3 py-2.5 font-medium">Stage</th>
              <th scope="col" className="text-right px-3 py-2.5 font-medium">Debtors</th>
              <th scope="col" className="text-right px-3 py-2.5 font-medium">Balance</th>
              <th scope="col" className="text-left px-3 py-2.5 font-medium">AI Agent</th>
              <th scope="col" className="text-left px-3 py-2.5 font-medium">Channel mix</th>
              <th scope="col" className="text-left px-3 py-2.5 font-medium">Status</th>
              <th scope="col" className="text-right px-3 py-2.5 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {AI_CAMPAIGNS.map((c) => {
              const ag = VOICE_AGENTS.find((a) => a.id === c.agent);
              return (
                <tr key={c.id} className="border-t border-border hover:bg-muted/40">
                  <td className="px-3 py-2"><div><p className="font-medium">{c.name}</p><p className="text-[10px] text-muted-foreground font-mono-num">{c.id}</p></div></td>
                  <td className="px-3 py-2 text-[12px] text-muted-foreground">{c.client}<br /><span className="font-mono-num">{c.portfolio}</span></td>
                  <td className="px-3 py-2 text-[12px]">{c.stage}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{formatNumber(c.debtors)}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{formatSAR(c.balance, { compact: true })}</td>
                  <td className="px-3 py-2 text-[12px]">{ag ? ag.name : '—'}</td>
                  <td className="px-3 py-2 text-[11px] text-muted-foreground">{c.channelMix}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className={cn('text-[10px]', STATUS_TONE[c.status])}>{c.status}</Badge></td>
                  <td className="px-3 py-2 text-right"><Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={() => setDrawer(c)}><Settings className="h-3 w-3 mr-1" aria-hidden="true" />Configure</Button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Sheet open={!!drawer} onOpenChange={(o) => !o && setDrawer(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          {drawer && (
            <>
              <SheetHeader><SheetTitle>{drawer.name}</SheetTitle></SheetHeader>
              <div className="space-y-4 mt-4">
                <Section label="Eligible debtor segment">{drawer.stage} · {drawer.client}</Section>
                <Section label="Assigned AI agent">{VOICE_AGENTS.find((a) => a.id === drawer.agent)?.name || '—'}</Section>
                <Section label="Channel mix">{drawer.channelMix}</Section>

                <div className="grid grid-cols-2 gap-2">
                  <ConfigField label="Contact window" value="09:00 – 21:00 KSA" />
                  <ConfigField label="Max attempts / debtor" value="10 / 30d" />
                  <ConfigField label="Fallback to WhatsApp" value="After 2 no-answers" />
                  <ConfigField label="Fallback to human" value="Hardship · Dispute · Angry" />
                  <ConfigField label="Payment link" value="Sent after PTP" />
                  <ConfigField label="Legal escalation" value="120+ DPD · supervisor" />
                </div>

                <div className="space-y-2">
                  <ToggleRow label="Supervisor approval required" defaultOn />
                  <ToggleRow label="Send Nafath verification before disclosure" defaultOn />
                  <ToggleRow label="Skip-trace on wrong-party" defaultOn />
                  <ToggleRow label="Auto-pause on compliance flag" defaultOn />
                </div>

                <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90"><Send className="h-4 w-4 mr-1.5" aria-hidden="true" />Save assignment</Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </VoiceLayout>
  );
}

const Section = ({ label, children }) => (
  <div>
    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className="text-[13px] mt-0.5">{children}</p>
  </div>
);
const ConfigField = ({ label, value }) => (
  <div className="rounded-md bg-muted/40 p-2.5">
    <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className="text-[12px] font-medium mt-0.5">{value}</p>
  </div>
);
const ToggleRow = ({ label, defaultOn }) => (
  <div className="flex items-center justify-between p-2.5 rounded-md border border-border bg-muted/30">
    <span className="text-[12.5px]">{label}</span>
    <Switch defaultChecked={defaultOn} aria-label={label} />
  </div>
);
